import type { DomainEvent } from '@domain/events.ts'
import type { FamilyState } from '@domain/state.ts'
import { childrenOf } from '@domain/state.ts'
import { insightsFor, type TaskInsight } from '@domain/projections/insights.ts'
import { balance, intentoDe, loQueCuesta, type Intento } from '@domain/rules/mates.ts'
import { es } from '../strings/es.ts'

/**
 * What the log knows and nobody had read.
 *
 * "It is not working for him" is not something anyone can act on. This answers
 * the four questions that are: does he start it, does he finish it, which step
 * does he stop at, and at what time of day does it actually happen.
 */
function Bar({ value, total, tone }: { value: number; total: number; tone: string }) {
  const percent = total === 0 ? 0 : Math.round((value / total) * 100)
  return (
    <span className="bg-rule block h-2 w-full overflow-hidden rounded-full">
      <span
        className="block h-full rounded-full"
        style={{ width: `${percent}%`, background: tone }}
      />
    </span>
  )
}

function TaskCard({ task }: { task: TaskInsight }) {
  if (task.daysStarted === 0) return null
  return (
    <li className="border-rule bg-surface m-0 rounded-xl border p-4">
      <p className="m-0 font-bold">{task.name}</p>

      <div className="mt-3 grid grid-cols-3 gap-3 text-center">
        <span>
          <span className="block text-2xl font-extrabold">{task.daysStarted}</span>
          <span className="text-muted block text-xs font-bold uppercase">
            {es.insights.started}
          </span>
        </span>
        <span>
          <span className="text-mint block text-2xl font-extrabold">{task.daysFinished}</span>
          <span className="text-muted block text-xs font-bold uppercase">
            {es.insights.finished}
          </span>
        </span>
        <span>
          <span className="text-coral block text-2xl font-extrabold">{task.daysAbandoned}</span>
          <span className="text-muted block text-xs font-bold uppercase">
            {es.insights.abandoned}
          </span>
        </span>
      </div>

      <span className="mt-3 block">
        <Bar value={task.daysFinished} total={task.daysStarted} tone="#26765a" />
      </span>

      {task.stickingPoint ? (
        <p className="bg-coral-soft text-coral m-0 mt-3 rounded-lg p-3 text-sm font-bold">
          {es.insights.stuck(
            task.stickingPoint.text,
            task.stickingPoint.position,
            task.stickingPoint.times,
          )}
        </p>
      ) : (
        <p className="text-mint m-0 mt-3 text-sm font-bold">{es.insights.allGood}</p>
      )}

      <p className="text-muted m-0 mt-2 text-sm">
        {task.typicalHour !== undefined ? es.insights.hour(task.typicalHour) : ''}
        {task.typicalHour !== undefined && task.medianMinutesToFinish !== undefined ? ' · ' : ''}
        {task.medianMinutesToFinish !== undefined
          ? es.insights.takes(task.medianMinutesToFinish)
          : ''}
      </p>
    </li>
  )
}

export function Insights({
  state,
  events,
  day,
  onBack,
}: {
  state: FamilyState
  events: readonly DomainEvent[]
  day: string
  onBack: () => void
}) {
  const children = childrenOf(state)

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-16">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight">{es.insights.title}</h1>
      <p className="text-muted mt-0 mb-8 text-sm">{es.insights.window(14)}</p>

      {children.map((child) => {
        const reading = insightsFor(state, events, child.id, day as never, 14)
        const withActivity = reading.tasks.filter((t) => t.daysStarted > 0)
        // El repaso de mates se lee del registro y no de la proyección: es
        // material de estudio, no estado de la casa.
        const repaso: readonly Intento[] = events.flatMap((event) =>
          event.type === 'PracticeFinished' && event.payload.memberId === child.id
            ? event.payload.items.map(intentoDe)
            : [],
        )
        const cuenta = balance(repaso)
        const cuesta = loQueCuesta(repaso, 3)
        return (
          <section key={child.id} className="mb-10">
            <h2 className="m-0 mb-1 text-xl font-extrabold" style={{ color: child.accent }}>
              {child.emoji} {child.name}
            </h2>
            <p className="text-muted mt-0 mb-4 text-sm font-bold">
              {es.insights.active(reading.daysWithAnyActivity)} ·{' '}
              {es.insights.afternoon(reading.afternoonRoutineDays)}
            </p>

            {repaso.length > 0 ? (
              <div className="border-rule bg-surface mb-3 rounded-xl border p-4">
                <p className="m-0 font-bold">{es.insights.mates}</p>
                <p className="text-muted m-0 mt-1 text-sm">
                  {es.insights.matesCuenta(cuenta.aciertos, repaso.length, cuenta.lentos)}
                </p>
                {cuenta.conAyuda > 0 ? (
                  <p className="text-muted m-0 mt-1 text-sm">
                    {es.insights.matesConAyuda(cuenta.conAyuda)}
                  </p>
                ) : null}
                {cuesta.length > 0 ? (
                  <ul className="mt-3 grid list-none gap-1 p-0">
                    {cuesta.map((flojo) => (
                      <li key={flojo.destreza} className="m-0 text-sm font-bold">
                        {es.insights.matesFlojo(flojo.titulo, flojo.fallos, flojo.lentos)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-mint m-0 mt-2 text-sm font-bold">{es.insights.matesLimpio}</p>
                )}
              </div>
            ) : null}

            {withActivity.length === 0 ? (
              <p className="text-muted m-0">{es.insights.nothing}</p>
            ) : (
              <>
                <ul className="grid list-none gap-3 p-0">
                  {withActivity.map((task) => (
                    <TaskCard key={task.taskId} task={task} />
                  ))}
                </ul>
                <p className="text-muted mt-4 mb-0 text-sm">{es.insights.readMore}</p>
              </>
            )}
          </section>
        )
      })}
    </div>
  )
}
